// Modal opened by a transaction action button whose handler is 'report':
// hosts the shared ReportViewer with parameters pre-resolved from the clicked
// row (the action's ``report.param_map``). The host adapter is wrapped so
// the mapped params override the report's own defaults — the criteria form
// shows them prefilled and auto-run kicks in when nothing is missing.
//
// Rendered by the shared TxnScreen (list 3-dot menu) and FormRenderer
// (open-record action bar) on every surface that supplies
// TxnRuntime.makeReportAdapter.

import { useMemo } from 'react'
import ReportViewer from './ReportViewer'
// The "fixed params override the report's defaults" adapter wrapper is shared
// with the smart-page / dashboard report embeds — see withFixedParams.ts.
import withFixedParams from './withFixedParams'
import type { ReportRuntimeAdapter } from './types'

interface Props {
  title?: string
  project: string
  objName: string
  adapter: ReportRuntimeAdapter
  /** Values resolved from the action's param_map, keyed by prompt name. */
  params?: Record<string, unknown>
  onClose: () => void
}

const overlayStyle: React.CSSProperties = {
  position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
  display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
}

export default function ReportActionModal({
  title, project, objName, adapter, params, onClose,
}: Props) {
  const wrapped = useMemo(
    () => (params && Object.keys(params).length
      ? withFixedParams(adapter, params)
      : adapter),
    [adapter, params],
  )
  return (
    <div style={overlayStyle} onClick={onClose}>
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: 'var(--color-surface, #fff)', color: 'var(--color-text, #111)',
          borderRadius: 8, width: 'min(980px, 94vw)', height: '88vh',
          display: 'flex', flexDirection: 'column', overflow: 'hidden',
          boxShadow: '0 10px 40px rgba(0,0,0,0.3)',
        }}>
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          padding: '10px 16px', borderBottom: '1px solid var(--color-border, #e5e5e5)',
          flex: '0 0 auto',
        }}>
          <span style={{ fontWeight: 600, fontSize: '0.9625rem' }}>{title || 'Report'}</span>
          <button
            onClick={onClose}
            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.1625rem', color: 'var(--color-text-secondary, #666)' }}
            title="Close">×</button>
        </div>
        <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          <ReportViewer
            key={`${project}/${objName}`}
            project={project}
            objName={objName}
            adapter={wrapped}
          />
        </div>
      </div>
    </div>
  )
}
